import { isLeft } from 'fp-ts/Either';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { StatCard } from '../components/StatCard';
import type { AppSpotlightController } from '../useAppSpotlight';
import type { ReportingSummary } from '../types';
import { isoDateDaysAgo, todayIsoDate } from '../utils';

interface ReportingTabProps {
  controller: AppSpotlightController;
}

const EVENT_LABELS: Record<string, string> = {
  impression: 'Impressions',
  tap: 'Taps',
  detail_view: 'Detail views',
  install_cta: 'Install CTA',
  install_success: 'Installs',
  other: 'Other',
};

const VARIANT_LABELS: Record<string, string> = {
  editorial: 'Editorial',
  personalized: 'Personalized',
  unknown: 'Unknown',
};

function eventLabel(type: string) {
  return EVENT_LABELS[type] ?? type;
}

function variantLabel(variant: string) {
  return VARIANT_LABELS[variant] ?? variant;
}

export function ReportingTab({ controller }: ReportingTabProps) {
  const [dateFrom, setDateFrom] = useState(() => isoDateDaysAgo(14));
  const [dateTo, setDateTo] = useState(() => todayIsoDate());
  const [weekId, setWeekId] = useState('');
  const [summaries, setSummaries] = useState<ReportingSummary[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const handleLoad = async () => {
    const result = await controller.loadReporting({
      dateFrom: new Date(`${dateFrom}T00:00:00`).toISOString(),
      dateTo: new Date(`${dateTo}T23:59:59`).toISOString(),
      weekId: weekId.trim() || undefined,
    });
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    setSummaries(result.right);
    setHasLoaded(true);
  };

  const sumByType = (type: string) =>
    summaries
      .filter((row) => row.eventType === type)
      .reduce((total, row) => total + row.events, 0);

  const impressions = sumByType('impression');
  const taps = sumByType('tap');
  const installs = sumByType('install_success');
  const ctr =
    impressions > 0 ? `${((installs / impressions) * 100).toFixed(1)}%` : '—';

  const variants = Array.from(new Set(summaries.map((row) => row.variant)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-neutral-800/60 p-4">
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-neutral-400">From</span>
          <input
            aria-label="Report Date From"
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            className="h-9 rounded-lg border border-neutral-800 bg-neutral-900/50 px-3 text-sm text-white focus:border-teal-500/60 focus:outline-none"
          />
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-neutral-400">To</span>
          <input
            aria-label="Report Date To"
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            className="h-9 rounded-lg border border-neutral-800 bg-neutral-900/50 px-3 text-sm text-white focus:border-teal-500/60 focus:outline-none"
          />
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-neutral-400">
            Week ID (optional)
          </span>
          <input
            aria-label="Report Week ID"
            value={weekId}
            onChange={(event) => setWeekId(event.target.value)}
            placeholder="All weeks"
            className="h-9 w-40 rounded-lg border border-neutral-800 bg-neutral-900/50 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none"
          />
        </label>
        <Button variant="outline" size="sm" onClick={() => void handleLoad()}>
          Load
        </Button>
      </div>

      {!hasLoaded ? (
        <div className="rounded-xl border border-dashed border-neutral-800 px-6 py-12 text-center text-sm text-neutral-500">
          Choose a range and load Spotlight telemetry.
        </div>
      ) : summaries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-800 px-6 py-12 text-center text-sm text-neutral-500">
          No telemetry for this range.
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Impressions"
              value={impressions.toLocaleString()}
            />
            <StatCard label="Taps" value={taps.toLocaleString()} />
            <StatCard
              label="Installs"
              value={installs.toLocaleString()}
              variant="teal"
            />
            <StatCard
              label="Install rate"
              value={ctr}
              hint="installs ÷ impressions"
            />
          </div>

          {variants.map((variant) => {
            const rows = summaries.filter((row) => row.variant === variant);
            return (
              <div
                key={variant}
                className="overflow-hidden rounded-xl border border-neutral-800/60"
              >
                <div className="border-b border-neutral-800/60 px-4 py-2.5 text-sm font-medium text-neutral-200">
                  {variantLabel(variant)}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="border-b border-neutral-800/60 text-left text-xs uppercase tracking-wide text-neutral-500">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Week</th>
                        <th className="px-4 py-2.5 font-medium">Experiment</th>
                        <th className="px-4 py-2.5 font-medium">Event</th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Events
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Unique apps
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800/60">
                      {rows.map((row, index) => (
                        <tr
                          key={`${row.weekId}-${row.experimentId}-${row.eventType}-${index}`}
                        >
                          <td className="px-4 py-2.5 text-neutral-200">
                            {row.weekId ?? 'Unknown'}
                          </td>
                          <td className="px-4 py-2.5 text-neutral-400">
                            {row.experimentId ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-neutral-300">
                            {eventLabel(row.eventType)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-neutral-100">
                            {row.events.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-neutral-100">
                            {row.uniqueApps.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
